import React, { useState, useEffect, useRef, useCallback } from 'react';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { checkinAPI } from '../../services/api';
import { apiErrorMessage } from '../../utils/apiError';

type KioskState = 'email' | 'loading' | 'confirm' | 'confirming' | 'success' | 'error';

const INACTIVITY_TIMEOUT_MS = 30_000; // 30 seconds

const Kiosk: React.FC = () => {
  const [state, setState] = useState<KioskState>('email');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [bookingTime, setBookingTime] = useState('');
  const [bookingDate, setBookingDate] = useState('');
  const [lookupToken, setLookupToken] = useState('');
  const [error, setError] = useState('');
  const inactivityTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset to initial state
  const resetKiosk = useCallback(() => {
    setState('email');
    setEmail('');
    setFirstName('');
    setBookingTime('');
    setBookingDate('');
    setLookupToken('');
    setError('');
  }, []);

  // Restart inactivity timer on any user interaction
  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimer.current) {
      clearTimeout(inactivityTimer.current);
    }
    // Only auto-clear on non-email states (don't clear while typing)
    if (state !== 'email') {
      inactivityTimer.current = setTimeout(resetKiosk, INACTIVITY_TIMEOUT_MS);
    }
  }, [state, resetKiosk]);

  // Set up inactivity detection
  useEffect(() => {
    const events = ['touchstart', 'mousedown', 'keydown'];
    events.forEach(event => document.addEventListener(event, resetInactivityTimer));

    return () => {
      events.forEach(event => document.removeEventListener(event, resetInactivityTimer));
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    };
  }, [resetInactivityTimer]);

  // Start timer when leaving email screen
  useEffect(() => {
    if (state !== 'email') {
      resetInactivityTimer();
    }
  }, [state, resetInactivityTimer]);

  // Auto-reset from success after 10 seconds
  useEffect(() => {
    if (state === 'success') {
      const timer = setTimeout(resetKiosk, 10_000);
      return () => clearTimeout(timer);
    }
  }, [state, resetKiosk]);

  const handleLookup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setState('loading');
    setError('');

    try {
      const data = await checkinAPI.lookup(email);
      setFirstName(data.patient_first_name);
      setBookingTime(data.booking_time);
      setBookingDate(data.booking_date);
      setLookupToken(data.lookup_token);

      if (data.already_checked_in) {
        setState('success');
      } else {
        setState('confirm');
      }
    } catch (err: any) {
      setError(apiErrorMessage(err, 'No upcoming appointments found.'));
      setState('error');
    }
  };

  const handleConfirm = async () => {
    setState('confirming');
    try {
      await checkinAPI.confirm({ lookup_token: lookupToken });
      setState('success');
    } catch (err: any) {
      setError(apiErrorMessage(err, 'Unable to check in. Please see the front desk.'));
      setState('error');
    }
  };

  return (
    <div
      className="min-h-screen bg-clinic-surface flex flex-col items-center justify-center p-8"
      style={{ touchAction: 'manipulation' }}
    >
      {/* Logo */}
      <div className="mb-8">
        <img
          src="/logos/logo-primary.png"
          alt="Clinic Studios"
          className="h-12 mx-auto"
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />
      </div>

      <div className="w-full max-w-lg">

        {/* Email entry */}
        {state === 'email' && (
          <div className="bg-white rounded-card shadow-clinic-lg p-10 text-center">
            <h1 className="font-display text-3xl font-bold text-clinic-dark mb-3">
              Welcome
            </h1>
            <p className="text-clinic-muted font-body text-lg mb-8">
              Enter your email to check in.
            </p>

            <form onSubmit={handleLookup}>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                autoFocus
                autoComplete="email"
                className="w-full px-5 py-4 border-2 border-clinic-primary-lighter rounded-input font-body text-clinic-dark placeholder-clinic-muted focus:outline-none focus:ring-2 focus:ring-clinic-primary focus:border-transparent text-xl text-center mb-6"
              />
              <button
                type="submit"
                disabled={!email.trim()}
                className="w-full py-5 px-6 bg-clinic-primary hover:bg-clinic-primary-dark text-white font-body font-semibold text-xl rounded-button transition-colors disabled:opacity-50"
              >
                Find My Appointment
              </button>
            </form>
          </div>
        )}

        {/* Loading */}
        {state === 'loading' && (
          <div className="bg-white rounded-card shadow-clinic-lg p-10 text-center">
            <div className="animate-spin rounded-full h-14 w-14 border-b-2 border-clinic-primary mx-auto mb-4"></div>
            <p className="text-clinic-muted font-body text-lg">Looking up your appointment...</p>
          </div>
        )}

        {/* Confirm check-in */}
        {state === 'confirm' && (
          <div className="bg-white rounded-card shadow-clinic-lg p-10 text-center">
            <h1 className="font-display text-3xl font-bold text-clinic-dark mb-2">
              Hi {firstName}!
            </h1>
            <p className="text-clinic-muted font-body text-lg mb-8">
              Confirm your {bookingTime} appointment{bookingDate ? ` on ${bookingDate}` : ''}?
            </p>

            <button
              onClick={handleConfirm}
              className="w-full py-6 px-6 bg-clinic-primary hover:bg-clinic-primary-dark text-white font-body font-bold text-2xl rounded-button transition-colors"
            >
              I'm Here
            </button>

            <button
              onClick={resetKiosk}
              className="mt-4 text-clinic-muted hover:text-clinic-dark font-body text-sm transition-colors"
            >
              Not me? Go back
            </button>
          </div>
        )}

        {/* Confirming */}
        {state === 'confirming' && (
          <div className="bg-white rounded-card shadow-clinic-lg p-10 text-center">
            <div className="animate-spin rounded-full h-14 w-14 border-b-2 border-clinic-primary mx-auto mb-4"></div>
            <p className="text-clinic-muted font-body text-lg">Checking you in...</p>
          </div>
        )}

        {/* Success */}
        {state === 'success' && (
          <div className="bg-white rounded-card shadow-clinic-lg p-10 text-center">
            <div className="w-24 h-24 bg-clinic-primary rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircleIcon className="h-16 w-16 text-clinic-cream" />
            </div>
            <h1 className="font-display text-3xl font-bold text-clinic-dark mb-3">
              You're Checked In!
            </h1>
            <p className="text-clinic-muted font-body text-lg mb-2">
              Your clinician has been notified.
            </p>
            <p className="text-clinic-muted font-body">
              Please have a seat in the waiting area.
            </p>
          </div>
        )}

        {/* Error */}
        {state === 'error' && (
          <div className="bg-white rounded-card shadow-clinic-lg p-10 text-center">
            <h1 className="font-display text-2xl font-bold text-clinic-dark mb-3">
              Hmm, we couldn't find that.
            </h1>
            <p className="text-clinic-muted font-body text-lg mb-6">
              {error}
            </p>
            <button
              onClick={resetKiosk}
              className="w-full py-5 px-6 bg-clinic-primary hover:bg-clinic-primary-dark text-white font-body font-semibold text-xl rounded-button transition-colors"
            >
              Try Again
            </button>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="mt-8 text-center">
        <p className="text-xs text-clinic-muted font-body">
          &copy; {new Date().getFullYear()} Clinic Studios
        </p>
      </div>
    </div>
  );
};

export default Kiosk;
